import { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export const PLATFORM_PATH_RULES = [
  { prefix: '/dashboard', any: true },
  { prefix: '/superadmin/profile', any: true },
  { prefix: '/superadmin/users', permission: 'owner' },
  { prefix: '/companies', permission: 'companies' },
  { prefix: '/audit-firms', permission: 'owner' },
  { prefix: '/plans', permission: 'owner' },
  { prefix: '/modules', permission: 'owner' },
  { prefix: '/features', permission: 'owner' },
  { prefix: '/invoices', permission: 'billing' },
  { prefix: '/financial-dashboard', permission: 'billing' },
  { prefix: '/superadmin/credits', permission: 'billing' },
  { prefix: '/superadmin/xero', permission: 'billing' },
  { prefix: '/demos/settings', permission: 'demo_host' },
  { prefix: '/demos/analytics', permission: 'demos_all' },
  { prefix: '/demos/tours', permission: 'demos_all' },
  { prefix: '/demos', permission: ['demo_host', 'demos_all'] },
  { prefix: '/assistant', permission: 'owner' },
  { prefix: '/superadmin/user-search', permission: 'owner' },
  { prefix: '/leads', permission: 'owner' },
  { prefix: '/page-builder', permission: 'page_builder' },
  { prefix: '/superadmin/landing-pages', permission: 'landing_pages' },
  { prefix: '/email-templates', permission: 'owner' },
  { prefix: '/superadmin/help', permission: 'help_desk' },
  { prefix: '/superadmin/guided-tours', permission: 'owner' },
  { prefix: '/ndis', permission: 'owner' },
  { prefix: '/shift-logic', permission: 'owner' },
  { prefix: '/deployment-manager', permission: 'owner' },
  { prefix: '/site-settings', permission: 'site_settings' },
  { prefix: '/rbac-health', permission: 'owner' },
  { prefix: '/superadmin/support', permission: 'help_desk' },
  { prefix: '/settings', permission: 'owner' },
];

export function ownerHintFromUser(user) {
  return user?.platform_owner === true;
}

export function hasPlatformPermission(access, permission) {
  if (!access) return false;
  const owner = Boolean(access.is_platform_owner || access.all_permissions);
  if (permission === 'owner') return owner;
  if (owner) return true;
  const keys = Array.isArray(permission) ? permission : [permission];
  return keys.some((key) => key !== 'owner' && (access.permissions || []).includes(key));
}

export function pathAllowed(pathname, access) {
  if (!access) return true;
  if (access.is_platform_owner || access.all_permissions) return true;
  const path = (pathname || '/').split('?')[0];
  const rule = [...PLATFORM_PATH_RULES]
    .sort((a, b) => b.prefix.length - a.prefix.length)
    .find((item) => path === item.prefix || path.startsWith(`${item.prefix}/`));
  if (!rule || rule.any) return true;
  return hasPlatformPermission(access, rule.permission);
}

export function usePlatformAccess(user) {
  const [access, setAccess] = useState(null);
  const userId = user?.id || user?.user_id || '';
  const role = user?.role || '';
  const flaggedOwner = user?.platform_owner === true;

  useEffect(() => {
    if (role !== 'superadmin') {
      setAccess(null);
      return undefined;
    }
    let cancelled = false;
    const token = localStorage.getItem('access_token');
    axios
      .get(`${API}/api/superadmin-users/me/permissions`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then((res) => {
        if (!cancelled) setAccess(res.data);
      })
      .catch(() => {
        if (cancelled) return;
        const owner = flaggedOwner;
        setAccess({
          is_platform_owner: owner,
          all_permissions: owner,
          permissions: [],
        });
      });
    return () => {
      cancelled = true;
    };
  }, [userId, role, flaggedOwner]);

  return access;
}
